import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Api from '../Network';
import { message } from 'antd';
import { getErrorMessage } from '../utils/getErrorMessage';

export interface CustomerFilters {
  name?: string; // Changed from search to name to match backend
  status?: string;
  isDeleted?: boolean;
}

export interface CustomerPaging {
  page: number;
  pageSize: number;
}

export interface CustomerSorting {
  [key: string]: 1 | -1;
}

export interface CustomerListState {
  filters: CustomerFilters;
  paging: CustomerPaging;
  sorting: CustomerSorting;
}

type CustomerQueryOptions = {
  useQueryOptions?: any;
  baseURL?: string;
  initialFilters?: CustomerListState;
  disableErrorHandling?: boolean;
};

export function useCustomers(useListOptions?: CustomerQueryOptions) {
  const [filters, setFilters] = useState<CustomerListState>(
    useListOptions?.initialFilters || {
      filters: { name: '' },
      paging: { page: 1, pageSize: 20 },
      sorting: {},
    }
  );
  const [isDemoMode, setIsDemoMode] = useState(false);

  function setListFilters(path: string, value: any) {
    setFilters((filters) => {
      const newFilters = { ...filters } as any;
      const pathParts = path.split('.');
      let current = newFilters;

      for (let i = 0; i < pathParts.length - 1; i++) {
        current = current[pathParts[i]];
      }

      current[pathParts[pathParts.length - 1]] = value;
      return newFilters;
    });
  }

  useEffect(() => {
    setListFilters('paging', useListOptions?.initialFilters?.paging || { page: 1, pageSize: 20 });
  }, []); // Only run once on mount

  const queryResult = useQuery({
    queryKey: ['customers', filters],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      try {
        // Check if we have authentication token
        const token = localStorage.getItem('token');
        if (!token) {
          setIsDemoMode(true);
          throw new Error('No authentication token');
        }

        // Try to fetch from API first
        const params = new URLSearchParams();
        params.append('page', filters.paging.page.toString());
        params.append('per-page', filters.paging.pageSize.toString());

        // Add all filters in Laravel QueryBuilder format
        Object.entries(filters.filters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            params.append(`filter[${key}]`, value.toString());
          }
        });

        // Add deleted filter (special case)
        if (filters.filters.isDeleted) {
          params.append('deleted', '1');
        }

        if (Object.keys(filters.sorting).length > 0) {
          const [field, direction] = Object.entries(filters.sorting)[0];
          params.append('sort', field);
          params.append('order', direction === 1 ? 'asc' : 'desc');
        }

        const url = `api/admin/customers?${params.toString()}`;
        const response = await Api.get(url, {
          signal,
          baseURL: useListOptions?.baseURL,
        });
        return response;
      } catch (error) {
        // API call failed, return mock data
        let filteredData = [...([] as any)];

        // Apply search filter
        if (filters.filters.name) {
          const search = filters.filters.name.toLowerCase();
          filteredData = filteredData.filter(
            (customer) =>
              (customer.name || '').toLowerCase().includes(search) ||
              (customer.phone_number || '').includes(search) ||
              (customer.email || '').toLowerCase().includes(search)
          );
        }

        // Apply status filter
        if (filters.filters.status) {
          filteredData = filteredData.filter(
            (customer) => customer.status === filters.filters.status
          );
        }

        // Apply sorting
        if (Object.keys(filters.sorting).length > 0) {
          const [field, direction] = Object.entries(filters.sorting)[0];
          filteredData.sort((a, b) => {
            const aVal = (a as any)[field] || '';
            const bVal = (b as any)[field] || '';
            if (direction === 1) {
              return aVal > bVal ? 1 : -1;
            } else {
              return aVal < bVal ? 1 : -1;
            }
          });
        }

        // Apply pagination
        const startIndex = (filters.paging.page - 1) * filters.paging.pageSize;
        const endIndex = startIndex + filters.paging.pageSize;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        return {
          status: true,
          code: 200,
          data: {
            data: paginatedData,
            count: filteredData.length,
            total: filteredData.length,
            current_page: filters.paging.page,
            per_page: filters.paging.pageSize,
            last_page: Math.ceil(filteredData.length / filters.paging.pageSize),
          },
        };
      }
    },
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    ...useListOptions?.useQueryOptions,
  });

  useEffect(() => {
    if (queryResult.error && !useListOptions?.disableErrorHandling) {
      message.error(getErrorMessage(queryResult.error, 'Failed to load customers'));
    }
  }, [queryResult.error, useListOptions?.disableErrorHandling]);

  return {
    filters,
    setListFilters,
    isDemoMode,
    ...queryResult,
  };
}
